import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";
import WhatsAppFloat from "@/components/site/WhatsAppFloat";
import { Reveal } from "@/components/site/Reveal";
import { FileText, Globe, Loader2 } from "lucide-react";

export const useSiteContent = () => {
  const [content, setContent] = useState(null);
  useEffect(() => { api.get("/content").then(({ data }) => setContent(data)).catch(() => {}); }, []);
  return content;
};

export default function Conteudos() {
  const content = useSiteContent();
  const [items, setItems] = useState(null);

  useEffect(() => {
    api.get("/public/contents").then(({ data }) => setItems(data)).catch(() => setItems([]));
  }, []);

  return (
    <div className="min-h-screen bg-[#FDFBF7] flex flex-col" data-testid="conteudos-page">
      <Helmet>
        <title>Conteúdos sobre terapia online | Tacilaine Almeida</title>
        <meta name="description" content="Artigos e páginas sobre psicoterapia online, ansiedade, autoconhecimento e como funciona o atendimento psicológico online." />
        <link rel="canonical" href={`${window.location.origin}/conteudos`} />
      </Helmet>
      <Navbar content={content} />
      <main className="flex-1 max-w-3xl mx-auto w-full px-5 pt-32 pb-20">
        <Reveal>
          <p className="font-body text-xs tracking-[0.35em] uppercase text-[#C87A5E]">Conteúdos</p>
          <h1 className="font-display text-4xl sm:text-5xl text-[#2C332A] mt-3">Terapia online, em palavras simples</h1>
          <p className="font-body text-base text-[#4A5D4E] mt-4 leading-relaxed">
            Textos educativos sobre psicoterapia online, ansiedade, relacionamentos e autoconhecimento —
            para adultos em todo o Brasil.
          </p>
        </Reveal>

        <div className="mt-10 space-y-4">
          {items === null ? (
            <Loader2 className="w-6 h-6 animate-spin text-[#5B6E5B]" />
          ) : items.length === 0 ? (
            <p className="font-body text-sm text-[#7A8A7A]">Novos conteúdos em breve.</p>
          ) : (
            items.map((c, i) => (
              <Reveal key={c.id} delay={0.05 * i}>
                <Link to={c.type === "artigo" ? `/conteudos/${c.slug}` : `/${c.slug}`} data-testid="conteudos-item"
                  className="block rounded-2xl bg-white border border-[#5B6E5B]/10 p-6 transition-[transform,box-shadow,border-color] duration-300 hover:-translate-y-0.5 hover:shadow-lg hover:border-[#5B6E5B]/30">
                  <span className="inline-flex items-center gap-1.5 font-body text-[11px] uppercase tracking-widest text-[#C87A5E]">
                    {c.type === "artigo" ? <FileText className="w-3.5 h-3.5" /> : <Globe className="w-3.5 h-3.5" />}
                    {c.type === "artigo" ? "Artigo" : "Página"}
                  </span>
                  <h2 className="font-display text-2xl text-[#2C332A] mt-2">{c.title}</h2>
                  {c.excerpt && <p className="font-body text-sm text-[#7A8A7A] mt-2">{c.excerpt}</p>}
                </Link>
              </Reveal>
            ))
          )}
        </div>

        <Reveal delay={0.1}>
          <div className="mt-12 rounded-3xl bg-[#F5F0E6] p-8 text-center">
            <p className="font-display text-2xl text-[#2C332A]">Quer conversar sobre terapia online?</p>
            <p className="font-body text-sm text-[#7A8A7A] mt-2">Atendimento psicológico online, com sigilo e acolhimento.</p>
            <a href="/#contato" data-testid="conteudos-cta"
              className="inline-flex items-center gap-2 rounded-full bg-[#5B6E5B] px-7 py-3.5 mt-5 text-[#FDFBF7] font-body text-sm transition-colors hover:bg-[#4A5D4E]">
              Agendar uma conversa
            </a>
          </div>
        </Reveal>
      </main>
      <Footer content={content} />
      <WhatsAppFloat content={content} />
    </div>
  );
}
